import React, { useState, useEffect } from 'react';
import { BlogPost, BLOG_POSTS, BlogComment } from '../../data/blogs';
import { ASSET_IMAGES } from '../../assets/images';
import { SafeImage } from '../SafeImage';
import { buildCleanPath } from '../../utils/navigation';
import { ImageSelectorField } from './ImageSelectorField';
import {
  BookOpen,
  Plus,
  Search,
  Sparkles,
  Edit2,
  Trash2,
  Eye,
  Check,
  X,
  ExternalLink,
  Tag,
  Clock,
  Heart,
  MessageSquare,
  Copy,
  ChevronRight,
  Filter,
  Layers,
  ArrowRight,
  RefreshCw,
  AlertTriangle,
  Send
} from 'lucide-react';

interface AdminBlogManagerProps {
  onNavigate: (page: string, param?: string) => void;
}

const BLOG_IMAGE_PRESETS = [
  { label: 'Kenyan Markets & Retail', url: ASSET_IMAGES.blogKenyanMarkets },
  { label: 'Dairy Farming & Milk ATM', url: ASSET_IMAGES.blogDairyFarming },
  { label: 'Clean Water Purification', url: ASSET_IMAGES.blogCleanWater },
  { label: 'Fresh Milk On Tap', url: ASSET_IMAGES.blogFreshMilkOnTap },
  { label: 'Water RO Plant Commercial', url: ASSET_IMAGES.heroWater },
  { label: 'Milk Pasteurizer Tank', url: ASSET_IMAGES.milkPasteurizer }
];

export const AdminBlogManager: React.FC<AdminBlogManagerProps> = ({ onNavigate }) => {
  const [blogs, setBlogs] = useState<BlogPost[]>(BLOG_POSTS);
  const [isLoading, setIsLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [featuredFilter, setFeaturedFilter] = useState<'all' | 'featured' | 'standard'>('all');

  // Modal State for Add / Edit
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [modalTab, setModalTab] = useState<'details' | 'content' | 'takeaways' | 'seo'>('details');

  // Delete Confirmation Modal
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Form State
  const [formData, setFormData] = useState<Omit<BlogPost, 'id' | 'views' | 'likes' | 'comments'>>({
    slug: '',
    title: '',
    excerpt: '',
    category: 'Commercial Automation',
    author: {
      name: 'Saints Tech Team',
      role: 'Technical & Engineering Team, Saints Tech Solutions',
      avatarInitials: 'ST'
    },
    publishedDate: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
    readTime: '6 min read',
    image: BLOG_IMAGE_PRESETS[0].url,
    featured: false,
    keyTakeaways: [''],
    tableOfContents: [{ id: 'intro', title: 'Introduction & Kenyan Market Dynamics' }],
    contentSections: [
      {
        id: 'intro',
        heading: 'Introduction & Kenyan Market Dynamics',
        paragraphs: [''],
        highlightQuote: '',
        bulletPoints: ['']
      }
    ],
    relatedProductIds: ['milk-atm-100l', 'water-vending-4tap'],
    seo: {
      metaTitle: '',
      metaDescription: '',
      keywords: '',
      canonicalUrl: ''
    }
  });

  // AI Blog Generator State
  const [isAiModalOpen, setIsAiModalOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [aiCategory, setAiCategory] = useState<BlogPost['category']>('Commercial Automation');
  const [aiTargetAudience, setAiTargetAudience] = useState('Kenyan kiosk entrepreneurs and dairy cooperative managers');
  const [aiKeywords, setAiKeywords] = useState('Saints Tech Solutions, milk ATM Nairobi, reverse osmosis Kenya');
  const [isGeneratingAi, setIsGeneratingAi] = useState(false);
  const [aiSuccessNotice, setAiSuccessNotice] = useState<string | null>(null);

  // Fetch all blogs directly from the database API
  useEffect(() => {
    let isMounted = true;
    const fetchBlogsFromDb = async () => {
      try {
        setIsLoading(true);
        const res = await fetch('/api/blogs');
        if (res.ok) {
          const data = await res.json();
          if (data.success && Array.isArray(data.blogs) && isMounted) {
            setBlogs(data.blogs);
          }
        }
      } catch (e) {
        console.warn('Backend database blog fetch error', e);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    fetchBlogsFromDb();
    return () => { isMounted = false; };
  }, []);

  // Filtered Blog List
  const filteredBlogs = blogs.filter((b) => {
    const matchCat = categoryFilter === 'all' || b.category === categoryFilter;
    const matchFeatured =
      featuredFilter === 'all' ||
      (featuredFilter === 'featured' && b.featured) ||
      (featuredFilter === 'standard' && !b.featured);
    const q = searchQuery.toLowerCase().trim();
    const matchSearch =
      !q ||
      b.title.toLowerCase().includes(q) ||
      b.slug.toLowerCase().includes(q) ||
      b.excerpt.toLowerCase().includes(q) ||
      b.category.toLowerCase().includes(q) ||
      b.seo.keywords.toLowerCase().includes(q);
    return matchCat && matchFeatured && matchSearch;
  });

  // Handler: Open Add Modal
  const handleOpenAddModal = () => {
    setIsEditing(false);
    setEditingId(null);
    setModalTab('details');
    setFormData({
      slug: '',
      title: '',
      excerpt: '',
      category: 'Commercial Automation',
      author: {
        name: 'Saints Tech Team',
        role: 'Technical & Engineering Team, Saints Tech Solutions',
        avatarInitials: 'ST'
      },
      publishedDate: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
      readTime: '6 min read',
      image: BLOG_IMAGE_PRESETS[0].url,
      featured: false,
      keyTakeaways: [
        'Micro-dispensing unlocks affordable retail transactions from KSh 5 to KSh 500.',
        'Food-grade Stainless Steel AISI 304 contact surfaces prevent bacterial contamination.',
        'Embedded GSM telemetry provides daily SMS revenue and volume audits to business owners.'
      ],
      tableOfContents: [
        { id: 'overview', title: 'Commercial Landscape & Technology Shift' },
        { id: 'engineering', title: 'Stainless Steel AISI 304 Sanitary Design' },
        { id: 'roi', title: 'Business Economics & Kiosk Payback' }
      ],
      contentSections: [
        {
          id: 'overview',
          heading: 'Commercial Landscape & Technology Shift',
          paragraphs: [
            'Across Nairobi, Nakuru, and Eldoret, retail commerce is shifting rapidly towards computerized micro-dispensing. Customers bring reusable containers, eliminating single-use packaging costs while enjoying food-grade sanitary dispensing.'
          ],
          highlightQuote: 'Automated dispensing allows Kenyan entrepreneurs to achieve fast cash turnarounds with complete digital inventory control.',
          bulletPoints: [
            'Instant M-Pesa and cash sales tracking',
            'Tamper-proof digital totalizer preventing shrinkage',
            'KeBS sanitary aligned food-grade piping'
          ]
        },
        {
          id: 'engineering',
          heading: 'Stainless Steel AISI 304 Sanitary Design',
          paragraphs: [
            'Machinery manufactured at Saints Tech Solutions Juja Road facility incorporates certified food-grade Stainless Steel 304 surfaces with mirror-polished finishes, ensuring complete bacteriological safety.'
          ]
        },
        {
          id: 'roi',
          heading: 'Business Economics & Kiosk Payback',
          paragraphs: [
            'With daily volumes exceeding 150 Litres, kiosk owners recover their capital equipment expenditure within 90 to 120 operating days.'
          ]
        }
      ],
      relatedProductIds: ['milk-atm-100l', 'water-vending-4tap'],
      seo: {
        metaTitle: '',
        metaDescription: '',
        keywords: '',
        canonicalUrl: ''
      }
    });
    setIsModalOpen(true);
  };

  // Handler: Open Edit Modal
  const handleOpenEditModal = (blog: BlogPost) => {
    setIsEditing(true);
    setEditingId(blog.id);
    setModalTab('details');
    setFormData({
      slug: blog.slug,
      title: blog.title,
      excerpt: blog.excerpt,
      category: blog.category,
      author: { ...blog.author },
      publishedDate: blog.publishedDate,
      readTime: blog.readTime,
      image: blog.image,
      featured: Boolean(blog.featured),
      keyTakeaways: [...blog.keyTakeaways],
      tableOfContents: blog.tableOfContents ? [...blog.tableOfContents] : [],
      contentSections: blog.contentSections.map(s => ({
        ...s,
        paragraphs: [...s.paragraphs],
        bulletPoints: s.bulletPoints ? [...s.bulletPoints] : []
      })),
      relatedProductIds: blog.relatedProductIds ? [...blog.relatedProductIds] : [],
      seo: { ...blog.seo }
    });
    setIsModalOpen(true);
  };

  // Handler: Save Blog Post
  const handleSaveBlogPost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) return;

    const slug =
      formData.slug.trim() ||
      formData.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

    const cleanTakeaways = formData.keyTakeaways.filter(t => t.trim() !== '');
    const cleanSections = formData.contentSections.filter(s => s.heading.trim() !== '');

    const postPayload: BlogPost = {
      id: isEditing && editingId ? editingId : `blog-${Date.now()}`,
      slug,
      title: formData.title.trim(),
      excerpt: formData.excerpt.trim() || formData.title,
      category: formData.category,
      author: formData.author,
      publishedDate: formData.publishedDate || new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
      readTime: formData.readTime || '6 min read',
      image: formData.image || BLOG_IMAGE_PRESETS[0].url,
      featured: formData.featured,
      views: isEditing ? (blogs.find(b => b.id === editingId)?.views || 100) : 1,
      likes: isEditing ? (blogs.find(b => b.id === editingId)?.likes || 12) : 5,
      keyTakeaways: cleanTakeaways.length > 0 ? cleanTakeaways : ['Sanitary food-grade equipment engineered for Kenyan commercial conditions.'],
      tableOfContents: cleanSections.map(s => ({ id: s.id, title: s.heading })),
      contentSections: cleanSections.length > 0 ? cleanSections : [
        {
          id: 'section-1',
          heading: 'Technical Analysis & Market Opportunity',
          paragraphs: [formData.excerpt || 'Commercial machinery analysis by Saints Tech Solutions.']
        }
      ],
      relatedProductIds: formData.relatedProductIds,
      seo: {
        metaTitle: formData.seo.metaTitle || `${formData.title} | Saints Tech Solutions`,
        metaDescription: formData.seo.metaDescription || formData.excerpt.slice(0, 155),
        keywords: formData.seo.keywords || `${formData.title}, Saints Tech Solutions, Kenya, vending machines`,
        canonicalUrl: formData.seo.canonicalUrl || `https://saintstech.co.ke/blog/${slug}`
      },
      comments: isEditing ? (blogs.find(b => b.id === editingId)?.comments || []) : []
    };

    if (isEditing && editingId) {
      setBlogs(prev => prev.map(b => b.id === editingId ? postPayload : b));
      try {
        const res = await fetch(`/api/blogs/${encodeURIComponent(editingId)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(postPayload)
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.blog) {
            setBlogs(prev => prev.map(b => b.id === editingId ? data.blog : b));
          }
        }
      } catch (err) {
        console.error('Database update error:', err);
      }
    } else {
      setBlogs(prev => [postPayload, ...prev]);
      try {
        const res = await fetch('/api/blogs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(postPayload)
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.blog) {
            setBlogs(prev => prev.map(b => b.id === postPayload.id ? data.blog : b));
          }
        }
      } catch (err) {
        console.error('Database create error:', err);
      }
    }

    setIsModalOpen(false);
  };

  // Handler: Delete Blog Post
  const handleDeleteBlogPost = async (id: string) => {
    setBlogs(prev => prev.filter(b => b.id !== id && b.slug !== id));
    setDeleteConfirmId(null);
    try {
      await fetch(`/api/blogs/${id}`, { method: 'DELETE' });
    } catch (e) {
      console.warn('Backend delete error, local state updated', e);
    }
  };

  // Handler: Toggle Featured
  const handleToggleFeatured = async (post: BlogPost) => {
    const updated = { ...post, featured: !post.featured };
    setBlogs(prev => prev.map(b => b.id === post.id ? updated : b));
    try {
      await fetch(`/api/blogs/${post.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ featured: updated.featured })
      });
    } catch {
      // ignore
    }
  };

  // Handler: Run AI Blog Writer
  const handleGenerateAiBlog = async () => {
    if (!aiTopic.trim()) return;
    setIsGeneratingAi(true);
    setAiSuccessNotice(null);

    try {
      const res = await fetch('/api/ai/generate-blog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: aiTopic.trim(),
          category: aiCategory,
          targetAudience: aiTargetAudience,
          keywords: aiKeywords
        })
      });

      const data = await res.json();
      if (data.success && data.blog) {
        const generated = data.blog;
        const newPost: BlogPost = {
          id: `blog-ai-${Date.now()}`,
          slug: generated.slug || generated.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          title: generated.title,
          excerpt: generated.excerpt,
          category: (generated.category as any) || aiCategory,
          author: generated.author || {
            name: 'Saints Tech Team',
            role: 'Technical & Engineering Team, Saints Tech Solutions',
            avatarInitials: 'ST'
          },
          publishedDate: generated.publishedDate || new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
          readTime: generated.readTime || '6 min read',
          image: BLOG_IMAGE_PRESETS.find(p => p.label.toLowerCase().includes('water'))?.url || BLOG_IMAGE_PRESETS[0].url,
          featured: false,
          views: 65,
          likes: 12,
          keyTakeaways: generated.keyTakeaways || [],
          tableOfContents: generated.tableOfContents || [],
          contentSections: generated.contentSections || [],
          relatedProductIds: generated.relatedProductIds || ['milk-atm-100l', 'water-vending-4tap'],
          seo: generated.seo || {
            metaTitle: `${generated.title} | Saints Tech Solutions`,
            metaDescription: generated.excerpt,
            keywords: aiKeywords,
            canonicalUrl: `https://saintstech.co.ke/blog/${generated.slug}`
          },
          comments: []
        };

        setBlogs(prev => [newPost, ...prev]);
        setAiSuccessNotice(`Successfully drafted and published "${newPost.title}" via ${data.source || 'Gemini AI'}!`);
        setAiTopic('');
        // Sync to backend
        try {
          await fetch('/api/blogs', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(newPost)
          });
        } catch {
          // ignore
        }
      } else {
        alert('AI generation failed: ' + (data.error || 'Unknown error'));
      }
    } catch (err: any) {
      alert('Network error connecting to AI generation service: ' + err.message);
    } finally {
      setIsGeneratingAi(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Banner & Quick Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-slate-950/80 p-5 rounded-2xl border border-slate-800">
        <div>
          <h2 className="text-xl font-extrabold text-white tracking-tight flex items-center gap-2.5">
            <BookOpen className="w-5 h-5 text-amber-400" />
            <span>Technical Blog & Industry Insights Management</span>
          </h2>
          <p className="text-xs text-slate-400 mt-1 max-w-2xl">
            Publish, edit, and optimize commercial engineering guides, dairy cold-chain field notes, and water science blueprints for Kenyan entrepreneurs.
          </p>
        </div>

        <div className="flex items-center gap-2.5 shrink-0">
          <button
            type="button"
            onClick={() => setIsAiModalOpen(true)}
            className="inline-flex items-center gap-2 px-3.5 py-2 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white rounded-xl text-xs font-bold shadow-md shadow-orange-950/30 transition-all cursor-pointer"
          >
            <Sparkles className="w-4 h-4" />
            <span>AI Article Writer</span>
          </button>

          <button
            type="button"
            onClick={handleOpenAddModal}
            className="inline-flex items-center gap-2 px-3.5 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-xl text-xs font-bold shadow-sm shadow-sky-600/30 transition-colors cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span>New Article</span>
          </button>
        </div>
      </div>

      {/* Stats Quick Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Total Published</div>
          <div className="text-2xl font-extrabold text-white tabular-nums mt-1">{blogs.length} Articles</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Across 4 industrial domains</div>
        </div>

        <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800">
          <div className="text-[11px] font-bold uppercase tracking-wider text-amber-400">Featured Stories</div>
          <div className="text-2xl font-extrabold text-amber-400 tabular-nums mt-1">
            {blogs.filter(b => b.featured).length} Posts
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">Hero lead stories</div>
        </div>

        <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800">
          <div className="text-[11px] font-bold uppercase tracking-wider text-sky-400">Cumulative Readership</div>
          <div className="text-2xl font-extrabold text-white tabular-nums mt-1">
            {blogs.reduce((acc, curr) => acc + (curr.views || 0), 0).toLocaleString()} Views
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">East African visitors</div>
        </div>

        <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800">
          <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-400">Reader Reactions</div>
          <div className="text-2xl font-extrabold text-emerald-400 tabular-nums mt-1">
            {blogs.reduce((acc, curr) => acc + (curr.likes || 0), 0)} Likes
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">Entrepreneur feedback</div>
        </div>
      </div>

      {/* Search and Category Filters */}
      <div className="bg-slate-950/80 p-4 rounded-2xl border border-slate-800 space-y-3">
        <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
          <div className="relative flex-1 w-full">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search articles by title, slug, excerpt, keywords, or topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full text-xs pl-10 pr-4 py-2.5 bg-slate-900 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-hidden focus:ring-2 focus:ring-sky-500"
            />
          </div>

          <div className="flex items-center gap-2 w-full md:w-auto">
            <select
              value={featuredFilter}
              onChange={(e) => setFeaturedFilter(e.target.value as any)}
              className="text-xs px-3 py-2 bg-slate-900 border border-slate-800 rounded-xl text-slate-200 focus:outline-hidden focus:ring-2 focus:ring-sky-500"
            >
              <option value="all">All Post Statuses</option>
              <option value="featured">Featured Stories Only</option>
              <option value="standard">Standard Articles Only</option>
            </select>
          </div>
        </div>

        {/* Category Filters */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
          {[
            { id: 'all', label: 'All Domains' },
            { id: 'Commercial Automation', label: 'Commercial Automation' },
            { id: 'Dairy Technology', label: 'Dairy Technology' },
            { id: 'Water Purification', label: 'Water Purification' },
            { id: 'Urban Vending', label: 'Urban Vending' }
          ].map(cat => (
            <button
              key={cat.id}
              onClick={() => setCategoryFilter(cat.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                categoryFilter === cat.id
                  ? 'bg-sky-600 text-white'
                  : 'bg-slate-900 hover:bg-slate-850 text-slate-400 hover:text-slate-200'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Articles Table */}
      <div className="bg-slate-950/80 rounded-2xl border border-slate-800 overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="bg-slate-900/90 border-b border-slate-800 text-slate-400 font-bold uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-3.5 px-4">Article / Title</th>
                <th className="py-3.5 px-4">Category</th>
                <th className="py-3.5 px-4">Author & Date</th>
                <th className="py-3.5 px-4 text-center">Metrics</th>
                <th className="py-3.5 px-4 text-center">Featured</th>
                <th className="py-3.5 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-850 text-slate-300">
              {filteredBlogs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-16 text-center text-slate-400">
                    <div className="max-w-xs mx-auto space-y-2">
                      <BookOpen className="w-8 h-8 text-slate-600 mx-auto" />
                      <div className="text-sm font-semibold text-slate-300">No blog articles found</div>
                      <p className="text-xs text-slate-500">
                        Try adjusting your search criteria or generate a new article using the AI Article Writer.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredBlogs.map((blog) => (
                  <tr key={blog.id} className="hover:bg-slate-900/50 transition-colors">
                    <td className="py-3.5 px-4 max-w-sm">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-900 shrink-0 border border-slate-800">
                          <SafeImage
                            src={blog.image}
                            fallbackCategory="general"
                            alt={blog.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="min-w-0">
                          <div className="font-bold text-white text-xs truncate hover:text-sky-400 transition-colors">
                            {blog.title}
                          </div>
                          <div className="text-[11px] text-slate-400 font-mono mt-0.5 truncate">
                            /blog/{blog.slug}
                          </div>
                          <div className="text-[10px] text-slate-500 mt-0.5 truncate">
                            {blog.excerpt}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="py-3.5 px-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-850 text-sky-400 border border-slate-750">
                        {blog.category}
                      </span>
                    </td>

                    <td className="py-3.5 px-4 whitespace-nowrap text-xs">
                      <div className="text-slate-200 font-medium">{blog.author.name}</div>
                      <div className="text-[10px] text-slate-400 flex items-center gap-1.5 mt-0.5">
                        <span>{blog.publishedDate}</span>
                        <span>·</span>
                        <span>{blog.readTime}</span>
                      </div>
                    </td>

                    <td className="py-3.5 px-4 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center gap-3 text-xs text-slate-400">
                        <span className="flex items-center gap-1" title="Views">
                          <Eye className="w-3.5 h-3.5 text-slate-400" />
                          <span className="tabular-nums font-semibold">{blog.views || 0}</span>
                        </span>
                        <span className="flex items-center gap-1" title="Likes">
                          <Heart className="w-3.5 h-3.5 text-rose-400" />
                          <span className="tabular-nums font-semibold">{blog.likes || 0}</span>
                        </span>
                        <span className="flex items-center gap-1" title="Comments">
                          <MessageSquare className="w-3.5 h-3.5 text-sky-400" />
                          <span className="tabular-nums font-semibold">{blog.comments?.length || 0}</span>
                        </span>
                      </div>
                    </td>

                    <td className="py-3.5 px-4 text-center whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => handleToggleFeatured(blog)}
                        className={`text-[10px] font-bold px-2.5 py-1 rounded-full cursor-pointer transition-colors ${
                          blog.featured
                            ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30'
                            : 'bg-slate-850 text-slate-400 border border-slate-800 hover:text-slate-300'
                        }`}
                      >
                        {blog.featured ? '★ Featured Lead' : 'Standard'}
                      </button>
                    </td>

                    <td className="py-3.5 px-4 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1.5">
                        <a
                          href={buildCleanPath('blog-detail', blog.slug)}
                          target="_blank"
                          rel="noreferrer"
                          className="p-1.5 bg-slate-850 hover:bg-slate-800 text-slate-400 hover:text-white rounded-lg transition-colors cursor-pointer"
                          title="View live article"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>

                        <button
                          type="button"
                          onClick={() => handleOpenEditModal(blog)}
                          className="p-1.5 bg-slate-850 hover:bg-sky-600 hover:text-white text-slate-300 rounded-lg transition-colors cursor-pointer"
                          title="Edit article details and content"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>

                        <button
                          type="button"
                          onClick={() => setDeleteConfirmId(blog.id)}
                          className="p-1.5 bg-slate-850 hover:bg-rose-600 hover:text-white text-slate-400 rounded-lg transition-colors cursor-pointer"
                          title="Delete article"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ======================================================== */}
      {/* MODAL 1: ADD / EDIT BLOG POST */}
      {/* ======================================================== */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-xs overflow-y-auto">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl text-slate-200">
            {/* Modal Header */}
            <div className="p-5 border-b border-slate-800 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-sky-400" />
                  <span>{isEditing ? 'Edit Blog Article' : 'Create New Technical Article'}</span>
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Full control over headings, takeaways, technical sections, and search snippets.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="p-1.5 text-slate-400 hover:text-white rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Tab Switcher */}
            <div className="px-5 border-b border-slate-800 flex items-center gap-2 bg-slate-950/50">
              {[
                { id: 'details', label: '1. Basic Meta & Title' },
                { id: 'content', label: '2. Deep-Dive Sections' },
                { id: 'takeaways', label: '3. Key Takeaways' },
                { id: 'seo', label: '4. Kenyan SERP SEO' }
              ].map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setModalTab(t.id as any)}
                  className={`px-3 py-2.5 text-xs font-semibold border-b-2 transition-colors cursor-pointer ${
                    modalTab === t.id
                      ? 'border-sky-500 text-sky-400'
                      : 'border-transparent text-slate-400 hover:text-slate-200'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Modal Form Body */}
            <form onSubmit={handleSaveBlogPost} className="flex-1 overflow-y-auto p-5 space-y-5">
              {modalTab === 'details' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1">
                        Article Headline / Title *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.title}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData(prev => ({
                            ...prev,
                            title: val,
                            slug: prev.slug || val.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
                          }));
                        }}
                        placeholder="e.g. Revolutionizing Dairy Farming with Modern Milk ATMs in Kenya"
                        className="w-full text-xs px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1">
                        Clean URL Slug * (No '#' symbol)
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.slug}
                        onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                        placeholder="e.g. revolutionizing-dairy-farming-milk-atms-kenya"
                        className="w-full text-xs px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white font-mono focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1">
                        Category
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as any }))}
                        className="w-full text-xs px-3 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                      >
                        <option value="Commercial Automation">Commercial Automation</option>
                        <option value="Dairy Technology">Dairy Technology</option>
                        <option value="Water Purification">Water Purification</option>
                        <option value="Urban Vending">Urban Vending</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1">
                        Read Time
                      </label>
                      <input
                        type="text"
                        value={formData.readTime}
                        onChange={(e) => setFormData(prev => ({ ...prev, readTime: e.target.value }))}
                        placeholder="6 min read"
                        className="w-full text-xs px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                      />
                    </div>

                    <div className="flex items-center pt-5">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.featured}
                          onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                          className="w-4 h-4 rounded text-sky-600 focus:ring-sky-500 border-slate-700 bg-slate-950"
                        />
                        <span className="text-xs font-semibold text-slate-200">
                          Feature as Editorial Lead Story
                        </span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">
                      Lead Excerpt / Summary (2-3 sentences)
                    </label>
                    <textarea
                      rows={3}
                      value={formData.excerpt}
                      onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                      placeholder="Brief overview explaining what Kenyan retail operators or farmers will learn..."
                      className="w-full text-xs p-3 bg-slate-950 border border-slate-800 rounded-xl text-white focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                    />
                  </div>

                  {/* Article Header Image Upload and Selection */}
                  <ImageSelectorField
                    value={formData.image}
                    onChange={(newUrl) => setFormData(prev => ({ ...prev, image: newUrl }))}
                    label="Article Header Feature Image"
                    description="Upload custom photography from your workshop or select from uploaded gallery and presets."
                    categoryHint="blog"
                  />
                </div>
              )}

              {modalTab === 'content' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-xs font-bold text-white uppercase tracking-wider">
                        Technical Content Sections
                      </h4>
                      <p className="text-[11px] text-slate-400">
                        Add structured headings, in-depth paragraphs, pull quotes, and technical bullet points.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        const newId = `section-${formData.contentSections.length + 1}`;
                        setFormData(prev => ({
                          ...prev,
                          contentSections: [
                            ...prev.contentSections,
                            {
                              id: newId,
                              heading: 'New Technical Subsection',
                              paragraphs: [''],
                              highlightQuote: '',
                              bulletPoints: ['']
                            }
                          ]
                        }));
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-sky-400 rounded-lg text-xs font-semibold"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Add Section</span>
                    </button>
                  </div>

                  <div className="space-y-4">
                    {formData.contentSections.map((section, idx) => (
                      <div key={section.id || idx} className="p-4 bg-slate-950 rounded-xl border border-slate-800 space-y-3">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-sky-400">
                            Section {idx + 1}
                          </span>
                          {formData.contentSections.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  contentSections: prev.contentSections.filter((_, i) => i !== idx)
                                }));
                              }}
                              className="text-slate-500 hover:text-rose-400 p-1"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>

                        <div>
                          <label className="block text-[11px] text-slate-400 mb-1">Heading</label>
                          <input
                            type="text"
                            value={section.heading}
                            onChange={(e) => {
                              const val = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                contentSections: prev.contentSections.map((s, i) => i === idx ? { ...s, heading: val } : s)
                              }));
                            }}
                            placeholder="e.g. Food-Grade Stainless Steel & Sanitary Compliance"
                            className="w-full text-xs px-3 py-2 bg-slate-900 border border-slate-800 rounded-lg text-white"
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] text-slate-400 mb-1">
                            Paragraph Text (Multi-paragraph supported by newlines)
                          </label>
                          <textarea
                            rows={4}
                            value={section.paragraphs.join('\n\n')}
                            onChange={(e) => {
                              const paras = e.target.value.split('\n\n');
                              setFormData(prev => ({
                                ...prev,
                                contentSections: prev.contentSections.map((s, i) => i === idx ? { ...s, paragraphs: paras } : s)
                              }));
                            }}
                            placeholder="Detailed technical and practical analysis..."
                            className="w-full text-xs p-3 bg-slate-900 border border-slate-800 rounded-lg text-white"
                          />
                        </div>

                        <div>
                          <label className="block text-[11px] text-slate-400 mb-1">
                            Highlight Quote (Optional)
                          </label>
                          <input
                            type="text"
                            value={section.highlightQuote || ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                contentSections: prev.contentSections.map((s, i) => i === idx ? { ...s, highlightQuote: val } : s)
                              }));
                            }}
                            placeholder="Key quote for callout styling..."
                            className="w-full text-xs px-3 py-2 bg-slate-900 border border-slate-800 rounded-lg text-white italic"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {modalTab === 'takeaways' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-xs font-bold text-white uppercase tracking-wider">
                        Executive Key Takeaways
                      </h4>
                      <p className="text-[11px] text-slate-400">
                        Highlighted bullet points shown in the callout box at the top of the article.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          keyTakeaways: [...prev.keyTakeaways, '']
                        }));
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-sky-400 rounded-lg text-xs font-semibold"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Add Takeaway</span>
                    </button>
                  </div>

                  <div className="space-y-2.5">
                    {formData.keyTakeaways.map((takeaway, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="w-5 text-center text-xs font-bold text-slate-500 tabular-nums">
                          {idx + 1}.
                        </span>
                        <input
                          type="text"
                          value={takeaway}
                          onChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({
                              ...prev,
                              keyTakeaways: prev.keyTakeaways.map((t, i) => i === idx ? val : t)
                            }));
                          }}
                          placeholder="e.g. Reverse Osmosis removes 99.2% of dissolved borehole fluoride and mineral salinity."
                          className="flex-1 text-xs px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white"
                        />
                        {formData.keyTakeaways.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                keyTakeaways: prev.keyTakeaways.filter((_, i) => i !== idx)
                              }));
                            }}
                            className="p-2 text-slate-500 hover:text-rose-400"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {modalTab === 'seo' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">
                      Meta Title (Google SERP Tag)
                    </label>
                    <input
                      type="text"
                      value={formData.seo.metaTitle}
                      onChange={(e) => setFormData(prev => ({ ...prev, seo: { ...prev.seo, metaTitle: e.target.value } }))}
                      placeholder="e.g. Milk ATMs Revolutionizing Kenyan Dairy Farming | Saints Tech Solutions"
                      className="w-full text-xs px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">
                      Meta Description (Search Result Snippet)
                    </label>
                    <textarea
                      rows={3}
                      value={formData.seo.metaDescription}
                      onChange={(e) => setFormData(prev => ({ ...prev, seo: { ...prev.seo, metaDescription: e.target.value } }))}
                      placeholder="Learn how Saints Tech Solutions automated Milk ATMs and pasteurizers help Kenyan farmers double profit..."
                      className="w-full text-xs p-3 bg-slate-950 border border-slate-800 rounded-xl text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">
                      Target Keywords (Comma Separated)
                    </label>
                    <input
                      type="text"
                      value={formData.seo.keywords}
                      onChange={(e) => setFormData(prev => ({ ...prev, seo: { ...prev.seo, keywords: e.target.value } }))}
                      placeholder="milk ATM Kenya, dairy equipment Nairobi, pasteurizer Juja Road"
                      className="w-full text-xs px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white"
                    />
                  </div>

                  {/* Google SERP Preview Card */}
                  <div className="p-4 bg-slate-950 rounded-xl border border-slate-800 space-y-1.5">
                    <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">
                      Google Search Result Snippet Preview
                    </div>
                    <div className="text-xs text-sky-400 font-medium">
                      https://saintstech.co.ke › blog › {formData.slug || 'article-slug'}
                    </div>
                    <div className="text-sm font-semibold text-blue-400 hover:underline cursor-pointer">
                      {formData.seo.metaTitle || formData.title || 'Article Title Preview'}
                    </div>
                    <div className="text-xs text-slate-400 line-clamp-2">
                      {formData.seo.metaDescription || formData.excerpt || 'Article search snippet preview.'}
                    </div>
                  </div>
                </div>
              )}

              {/* Modal Footer Controls */}
              <div className="pt-4 border-t border-slate-800 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-semibold transition-colors"
                >
                  Cancel
                </button>

                <div className="flex items-center gap-2">
                  <button
                    type="submit"
                    className="inline-flex items-center gap-1.5 px-5 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-xl text-xs font-bold shadow-md shadow-sky-600/30 transition-colors cursor-pointer"
                  >
                    <Check className="w-4 h-4" />
                    <span>{isEditing ? 'Save Article' : 'Publish Article'}</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL 2: AI ARTICLE WRITER */}
      {/* ======================================================== */}
      {isAiModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-xs">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-xl p-6 shadow-2xl text-slate-200 space-y-5">
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white shadow-md shadow-orange-500/20">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-base font-extrabold text-white">Gemini AI Article Writer</h3>
                  <p className="text-xs text-slate-400">Generate complete Kenyan technical analyses with one prompt</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsAiModalOpen(false)}
                className="text-slate-400 hover:text-white p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {aiSuccessNotice && (
              <div className="p-3 bg-emerald-950/60 border border-emerald-500/40 rounded-xl text-xs text-emerald-300 flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>{aiSuccessNotice}</span>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Article Topic or Case Study Angle *
                </label>
                <input
                  type="text"
                  value={aiTopic}
                  onChange={(e) => setAiTopic(e.target.value)}
                  placeholder="e.g. Solar-powered milk chilling tanks for dairy farmers in Nyandarua"
                  className="w-full text-xs px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white focus:outline-hidden focus:ring-2 focus:ring-amber-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Domain Category</label>
                  <select
                    value={aiCategory}
                    onChange={(e) => setAiCategory(e.target.value as any)}
                    className="w-full text-xs px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white"
                  >
                    <option value="Commercial Automation">Commercial Automation</option>
                    <option value="Dairy Technology">Dairy Technology</option>
                    <option value="Water Purification">Water Purification</option>
                    <option value="Urban Vending">Urban Vending</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Target Audience</label>
                  <input
                    type="text"
                    value={aiTargetAudience}
                    onChange={(e) => setAiTargetAudience(e.target.value)}
                    className="w-full text-xs px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">
                  Target Search Terms (SEO Keywords)
                </label>
                <input
                  type="text"
                  value={aiKeywords}
                  onChange={(e) => setAiKeywords(e.target.value)}
                  placeholder="e.g. milk cooling tank Kenya, milk pasteurizer price Nairobi"
                  className="w-full text-xs px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white"
                />
              </div>

              {/* Suggested Topics Pill-free chips */}
              <div className="space-y-1.5 pt-1">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Quick Prompts:
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    'Smart Reverse Osmosis desalination for coastal Kenya boreholes',
                    'How twin-nozzle cooking oil ATMs double kiosk daily margins',
                    'KeBS hygiene requirements for commercial milk vending in Nairobi estates',
                    'Decentralized water refilling kiosks in high-density urban centers'
                  ].map((quick) => (
                    <button
                      key={quick}
                      type="button"
                      onClick={() => setAiTopic(quick)}
                      className="text-[11px] px-2.5 py-1 bg-slate-950 hover:bg-slate-850 border border-slate-800 rounded-lg text-slate-300 hover:text-white transition-colors text-left"
                    >
                      {quick}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-800 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setIsAiModalOpen(false)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-semibold"
              >
                Close
              </button>

              <button
                type="button"
                disabled={isGeneratingAi || !aiTopic.trim()}
                onClick={handleGenerateAiBlog}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white rounded-xl text-xs font-bold shadow-lg shadow-orange-950/40 disabled:opacity-50 transition-all cursor-pointer"
              >
                {isGeneratingAi ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Writing Engineering Post...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    <span>Generate & Publish</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL 3: DELETE CONFIRMATION */}
      {/* ======================================================== */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-xs">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-sm p-6 text-slate-200 text-center space-y-4 shadow-2xl">
            <div className="w-12 h-12 rounded-xl bg-rose-500/20 text-rose-400 mx-auto flex items-center justify-center">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div>
              <h4 className="text-base font-bold text-white">Delete Blog Post?</h4>
              <p className="text-xs text-slate-400 mt-1">
                Are you sure you want to remove this published article? This action cannot be undone.
              </p>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setDeleteConfirmId(null)}
                className="flex-1 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDeleteBlogPost(deleteConfirmId)}
                className="flex-1 px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-xl text-xs font-bold"
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
